import axios, { AxiosInstance } from 'axios';
import type {
  UserMapping,
  PhoneCheckResult,
  RequestAccessData,
  DealWithDetails,
  Comment,
  ApprovalAction,
  DealsListFilter,
  CabinetInfo,
} from '../types';

/**
 * Получить initDataRaw из Telegram Mini App SDK
 * В dev-режиме можно использовать mock
 */
function getInitDataRaw(): string {
  try {
    // Telegram Mini App передаёт initData через window
    const tgWebApp = (window as any).Telegram?.WebApp;
    if (tgWebApp?.initData) {
      return tgWebApp.initData;
    }
  } catch {
    // Вне Telegram — fallback
  }

  // Dev fallback — пустая строка (бэкенд можно настроить на пропуск в dev)
  return '';
}

/**
 * Axios-клиент с автоматической передачей Telegram initData
 */
const http: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 30000,
});

// Interceptor: добавляем Authorization header с initData
http.interceptors.request.use((cfg) => {
  const initData = getInitDataRaw();
  if (initData) {
    cfg.headers.Authorization = `tma ${initData}`;
  }
  return cfg;
});

// Interceptor: обработка ошибок
http.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const message = error.response?.data?.error || error.message;

      if (status === 401) {
        // Сессия невалидна — перенаправить на авторизацию
        window.location.hash = '#/auth';
      }

      return Promise.reject(new Error(message));
    }
    return Promise.reject(error);
  }
);

// ==========================================
// API методы
// ==========================================

export const api = {
  auth: {
    /** Проверить телефон в Б24 */
    async checkPhone(phone: string): Promise<PhoneCheckResult> {
      const { data } = await http.post('/auth/check-phone', { phone });
      return data;
    },

    /** Привязать Telegram к контакту Б24 */
    async login(params: {
      contactId: number;
      companyId: number;
      phone: string;
      fullName?: string;
      companyInn?: string;
    }): Promise<UserMapping> {
      const { data } = await http.post('/auth/login', params);
      return data;
    },

    /** Получить текущего пользователя */
    async me(): Promise<UserMapping> {
      const { data } = await http.get('/auth/me');
      return data;
    },

    /** Заявка нового клиента (лид) */
    async requestAccess(params: RequestAccessData): Promise<{ success: boolean; message: string }> {
      const { data } = await http.post('/auth/request-access', params);
      return data;
    },

    /** Выход из системы */
    async logout(): Promise<{ success: boolean }> {
      const { data } = await http.post('/auth/logout');
      return data;
    },
  },

  deals: {
    /** Список сделок компании */
    async list(filter?: DealsListFilter): Promise<DealWithDetails[]> {
      const params: Record<string, string> = {};
      if (filter?.actionRequired) params.actionRequired = 'true';
      if (filter?.categoryId !== undefined) params.categoryId = String(filter.categoryId);

      const { data } = await http.get('/deals', { params });
      return data;
    },

    /** Детали одной сделки */
    async get(dealId: number): Promise<DealWithDetails> {
      const { data } = await http.get(`/deals/${dealId}`);
      return data;
    },

    /** Создать заявку (с опциональными файлами) */
    async create(params: {
      title: string;
      description: string;
      categoryId: number;
      priority?: string;
      files?: File[];
    }): Promise<{ success: boolean; dealId: number }> {
      // Если есть файлы — отправляем FormData
      if (params.files && params.files.length > 0) {
        const formData = new FormData();
        formData.append('title', params.title);
        formData.append('description', params.description);
        formData.append('categoryId', String(params.categoryId));
        if (params.priority) formData.append('priority', params.priority);
        params.files.forEach((file) => {
          formData.append('files', file);
        });
        const { data } = await http.post('/deals', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        return data;
      }

      // Без файлов — обычный JSON
      const { title, description, categoryId, priority } = params;
      const { data } = await http.post('/deals', { title, description, categoryId, priority });
      return data;
    },

    /** Выполнить действие над сделкой */
    async action(
      dealId: number,
      action: ApprovalAction,
      body?: { message?: string; taskType?: string; rating?: number; ratingComment?: string }
    ): Promise<{ success: boolean; message?: string }> {
      const actionMap: Record<ApprovalAction, string> = {
        approve_cost: 'approve-cost',
        reject_cost: 'reject-cost',
        approve_tz: 'approve-tz',
        reject_tz: 'reject-tz',
        accept_work: 'accept-work',
        send_remarks: 'send-remarks',
      };
      const { data } = await http.post(`/deals/${dealId}/${actionMap[action]}`, body || {});
      return data;
    },

    /** Получить комментарии */
    async getComments(dealId: number): Promise<Comment[]> {
      const { data } = await http.get(`/deals/${dealId}/comments`);
      return data;
    },

    /** Добавить комментарий */
    async addComment(dealId: number, text: string): Promise<{ success: boolean }> {
      const { data } = await http.post(`/deals/${dealId}/comments`, { text });
      return data;
    },
  },

  company: {
    /** Информация о компании и менеджере из Б24 */
    async getInfo(): Promise<CabinetInfo> {
      const { data } = await http.get('/company-info');
      return data;
    },
  },

  callback: {
    /** Заявка на обратный звонок или покупку */
    async request(message?: string, requestType?: string): Promise<{ success: boolean; message: string }> {
      const { data } = await http.post('/callback-request', { message, requestType });
      return data;
    },
  },
};
